import * as CL from '@design-system/component-library';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { dsClass } from '../../common/constants/dsClasses';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import { companyMsg, nameMsg, orderMsg, phoneNumberMsg, t } from '../../common/i18n';
import { getCompanyName } from '../../common/utils/accountUtils';
import { deepEqual } from '../../common/utils/objectUtils';
import { isSubscriptionInSfdc } from '../../common/utils/subscriptionUtils';
import { SubscriptionStatusType } from '../../generated/api/subscriptionStatusType';
import { useAuth } from '../../public/site/AuthProvider';
import { ConsolidatedListView } from '../ConsolidatedListView/ConsolidatedListView';
import { DEFAULT_ITEMS_PER_PAGE, ListPagination, ListSearch } from '../Table';
import { Table } from '../Table/Table';
import { setDefaultItemsPerPage } from '../Table/tableUtils';
import type { SubscriptionHeader } from '../../generated/api/subscriptionHeader';
import type { State } from '../../selfservice/common/store';
import type { SubscriptionVoiceSearchParams } from '../Subscriptions/SubscriptionsVoice';

import './KukaSoittaaOrder.scss';

interface ValuesType {
  selected: string[];
  selectAll: boolean;
}

export interface KukaSoittaaOrderTableProps {
  subscriptionHeaders: SubscriptionHeader[];
  loading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  handleSubmit: (values: ValuesType) => void;
}

interface SubscriptionsTableProps {
  subscriptionHeaders: SubscriptionHeader[];
  loading: boolean;
  handleSubmit: (values: ValuesType) => void;
}

const SubscriptionsTable = ({ subscriptionHeaders, loading, handleSubmit }: SubscriptionsTableProps) => {
  const { authenticatedUser } = useAuth();
  const searchParams = useSearchParams<SubscriptionVoiceSearchParams>();
  const {
    register,
    watch,
    setValue,
    handleSubmit: onSubmit,
  } = useForm<ValuesType>({
    defaultValues: {
      selected: [],
      selectAll: false,
    },
  });

  const watchedValues = watch();

  const allIds = subscriptionHeaders
    .map(sub => sub.subscriptionUserFriendlyId)
    .filter((id): id is string => id !== undefined);

  const handleSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    let newSelected: string[];

    if (checked) {
      newSelected = [...watchedValues.selected, value];
    } else {
      newSelected = watchedValues.selected.filter(id => id !== value);
    }

    setValue('selected', newSelected);

    if (!checked && watchedValues.selectAll) {
      setValue('selectAll', false);
    }
    if (checked && newSelected.length === allIds.length) {
      setValue('selectAll', true);
    }
  };

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setValue('selectAll', checked);
    setValue('selected', checked ? allIds : []);
  };

  const columns = [
    {
      key: 'checkbox',
      label: (
        <input
          {...register('selectAll')}
          onChange={handleSelectAllChange}
          type="checkbox"
          checked={watchedValues.selectAll}
        />
      ),
    },
    { key: 'name', label: t.PNS1(nameMsg) },
    { key: 'phoneNumber', label: t.AGIJ(phoneNumberMsg) },
    { key: 'company', label: t.KJTS(companyMsg) },
  ];

  const rows = subscriptionHeaders.map((subHeader: SubscriptionHeader) => {
    return {
      checkbox: (
        <input
          onChange={handleSelectChange}
          value={subHeader.subscriptionUserFriendlyId}
          type="checkbox"
          checked={
            subHeader.subscriptionUserFriendlyId
              ? watchedValues.selected.includes(subHeader.subscriptionUserFriendlyId)
              : false
          }
        />
      ),
      name: subHeader.subscriptionContactName || subHeader.subscriptionPurposeOfUse,
      phoneNumber: subHeader.subscriptionUserFriendlyId,
      company: getCompanyName(authenticatedUser, subHeader.accountMasterId),
    };
  });

  const onFormSubmit = (values: ValuesType) => {
    handleSubmit(values);
  };

  return (
    <form onSubmit={onSubmit(onFormSubmit)}>
      <ConsolidatedListView
        authenticatedUser={authenticatedUser}
        listSearch={
          <div className="kukasoittaa-order-table__actions">
            <ListSearch
              onSearch={() => setDefaultItemsPerPage(new URLSearchParams(searchParams), DEFAULT_ITEMS_PER_PAGE)}
            />
            <CL.Button color="primary" type="submit">
              {t.AQOL(orderMsg)}
            </CL.Button>
          </div>
        }
        listElement={<Table columns={columns} rows={rows} isLoading={loading} />}
        listPagination={<ListPagination totalItems={subscriptionHeaders.length || 0} />}
      />
    </form>
  );
};

export const KukaSoittaaOrderTable = ({
  subscriptionHeaders,
  loading,
  error,
  setError,
  handleSubmit,
}: KukaSoittaaOrderTableProps) => {
  const subscriptionActions = useSelector(
    (state: State) => state.selfservice?.pendingSubscriptionActions?.items || [],
    deepEqual
  );

  const filteredSubscriptions = subscriptionHeaders.filter(
    sub =>
      sub.subscriptionStatus === SubscriptionStatusType.ACTIVE &&
      !subscriptionActions.some(action => action.subscriptionId === sub.subscriptionId) &&
      isSubscriptionInSfdc(sub)
  );

  if (!filteredSubscriptions.length) {
    return <div>{t.SJKT('No subscriptions')}</div>;
  }

  return (
    <>
      {error && <CL.Notification text={error} type="warning" onNotificationHide={() => setError(null)} />}
      <h3 className={dsClass.FONT_SIZE_LARGE}>
        {t.PK01('Select the subscriptions for which you want to order the Elisa Kuka Soittaa service:')}
      </h3>
      <SubscriptionsTable subscriptionHeaders={filteredSubscriptions} loading={loading} handleSubmit={handleSubmit} />
    </>
  );
};
